'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { activityApi, waitForAuthReady } from '@/lib/api';
import type { ActivityModuleCatalogEntry } from '@/components/event-workspace/activity-nav-config';
import { useResolvedActivityId } from '@/lib/resolve-static-param';

import { useEventWorkspace } from '@/components/event-workspace/event-workspace-context';

export type ActivityFrameworkWorkspaceState = {
  loading: boolean;
  errorStatus: number | null;
  errorMessage: string | null;
  modules: string[];
  moduleCatalog: ActivityModuleCatalogEntry[];
  widgets: string[];
  capabilities: Record<string, boolean>;
  abilities: Record<string, boolean>;
  frameworkKey: string | null;
  frameworkLabel: string | null;
  metrics: Record<string, unknown>;
  activity: Record<string, unknown> | null;
  refresh: () => void;
};

const ActivityFrameworkWorkspaceContext = createContext<ActivityFrameworkWorkspaceState | null>(null);

export function ActivityFrameworkWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const activityId = useResolvedActivityId();
  const { event } = useEventWorkspace();
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [moduleCatalog, setModuleCatalog] = useState<ActivityModuleCatalogEntry[]>([]);
  const [widgets, setWidgets] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<Record<string, boolean>>({});
  const [abilities, setAbilities] = useState<Record<string, boolean>>({});
  const [frameworkKey, setFrameworkKey] = useState<string | null>(null);
  const [frameworkLabel, setFrameworkLabel] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Record<string, unknown>>({});
  const [activity, setActivity] = useState<Record<string, unknown> | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!activityId) {
      setLoading(false);
      setErrorStatus(null);
      setErrorMessage(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorStatus(null);
    setErrorMessage(null);
    void (async () => {
      await waitForAuthReady();
      if (cancelled) return;
      try {
        const res = await activityApi.dashboard(activityId);
        if (cancelled) return;
        const data = (res.data?.data ?? {}) as Record<string, any>;
        setErrorStatus(null);
        setErrorMessage(null);
        setModules((data.modules as string[]) ?? []);
        setModuleCatalog((data.module_catalog as ActivityModuleCatalogEntry[]) ?? []);
        setWidgets((data.widgets as string[]) ?? []);
        setCapabilities((data.capabilities as Record<string, boolean>) ?? {});
        setAbilities((data.abilities as Record<string, boolean>) ?? {});
        setFrameworkKey((data.framework_key as string) ?? data.framework?.key ?? null);
        setFrameworkLabel((data.framework?.label as string) ?? data.activity?.framework_label ?? null);
        setMetrics((data.metrics as Record<string, unknown>) ?? {});
        setActivity((data.activity as Record<string, unknown>) ?? null);
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status ? Number(err.response.status) : null;
        const message = err?.response?.data?.message || err?.message || 'Failed to load activity dashboard';
        setErrorStatus(status);
        setErrorMessage(message);

        const list = Array.isArray((event as any)?.activities) ? (event as any).activities : [];
        const fallback = list.find((a: any) => a?.id === activityId) || null;
        if (fallback) {
          setActivity(fallback);
          setFrameworkKey((fallback.framework_key as string) ?? null);
          setFrameworkLabel((fallback.framework_label as string) ?? null);
        } else {
          setActivity(null);
          setFrameworkKey(null);
          setFrameworkLabel(null);
        }
        setModules([]);
        setModuleCatalog([]);
        setWidgets([]);
        setCapabilities({});
        setAbilities({});
        setMetrics({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activityId, tick, event]);

  const value = useMemo<ActivityFrameworkWorkspaceState>(
    () => ({
      loading,
      errorStatus,
      errorMessage,
      modules,
      moduleCatalog,
      widgets,
      capabilities,
      abilities,
      frameworkKey,
      frameworkLabel,
      metrics,
      activity,
      refresh: () => setTick((n) => n + 1),
    }),
    [
      loading,
      errorStatus,
      errorMessage,
      modules,
      moduleCatalog,
      widgets,
      capabilities,
      abilities,
      frameworkKey,
      frameworkLabel,
      metrics,
      activity,
    ],
  );

  return (
    <ActivityFrameworkWorkspaceContext.Provider value={value}>
      {children}
    </ActivityFrameworkWorkspaceContext.Provider>
  );
}

export function useActivityFrameworkWorkspace(): ActivityFrameworkWorkspaceState {
  const ctx = useContext(ActivityFrameworkWorkspaceContext);
  if (!ctx) {
    return {
      loading: false,
      errorStatus: null,
      errorMessage: null,
      modules: [],
      moduleCatalog: [],
      widgets: [],
      capabilities: {},
      abilities: {},
      frameworkKey: null,
      frameworkLabel: null,
      metrics: {},
      activity: null,
      refresh: () => undefined,
    };
  }
  return ctx;
}
